import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './GroupManager.module.css';
import DetailedGroupModal from './DetailedGroupModal';
import { FIND_GROUPS_INVENTORY, FIND_GROUPS_ITEM_INVENTORY, ITEM_UNGROUP_URL } from '../constant/apiConstants';

const GroupManager = () => {
  const [groups, setGroups] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupItems, setGroupItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showDetailedModal, setShowDetailedModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'grid' or 'table'

  const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');

    return {
      user: user ? JSON.parse(user) : null,
      token: token || null
    };
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const { user, token } = getUserData();
      if (!user || !user.storeId) {
        setError('Store ID not found');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${FIND_GROUPS_INVENTORY}`, {
        params: { storeId: user.storeId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        setGroups(response.data.payload || []);
      } else {
        setError(response.data.message || 'Failed to fetch groups');
      }
    } catch (err) {
      setError(err.message || 'Error fetching groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupItems = async (generalGroupId) => {
    try {
      const { user, token } = getUserData();
      
      const response = await axios.get(`${FIND_GROUPS_ITEM_INVENTORY}`, {
        params: { 
          generalGroupId: generalGroupId,
          storeId: user.storeId 
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        setGroupItems(prev => ({
          ...prev,
          [generalGroupId]: response.data.payload || []
        }));
      }
    } catch (err) {
      console.error('Error fetching group items:', err);
    }
  };

  const toggleGroup = async (group) => {
    if (expandedGroup === group.generalGroupId) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(group.generalGroupId);
      if (!groupItems[group.generalGroupId]) {
        await fetchGroupItems(group.generalGroupId);
      }
    }
  };

  const handleAddItem = (group) => {
    setSelectedGroup(group);
    setShowAddItemModal(true);
  };

  const handleShowDetailed = async (group) => {
    setSelectedGroup(group);
    // Fetch items if not already loaded
    if (!groupItems[group.generalGroupId]) {
      await fetchGroupItems(group.generalGroupId);
    }
    setShowDetailedModal(true);
  };

  const closeModal = () => {
    setShowAddItemModal(false);
    setSelectedGroup(null);
  };

  const closeDetailedModal = () => {
    setShowDetailedModal(false);
    setSelectedGroup(null);
  };

 const handleUngroup = async (item) => {
  try {
    const { user, token } = getUserData();
    const apiRequestBody = {
      user,
      token,
      generalGroupId: item.generalGroupId,
      groupId: item.group_id,
    };
    // Call the backend API to ungroup the item
    const response = await axios.post(`${ITEM_UNGROUP_URL}`, apiRequestBody);
    if (response.data.status === 'success') {
      await fetchGroupItems(item.generalGroupId);
    } else {
      alert('Failed to ungroup item: ' + response.data.message);
    }
  } catch (error) {
    console.error('Error ungrouping item:', error);
    alert('Error ungrouping item.');
  }
};


  if (loading) {
    return <div className={styles.loading}>Loading groups...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Group Manager</h1>
        <p className={styles.subtitle}>Manage your inventory groups and items</p>
      </div>

      <div className={styles.groupsList}>
        {groups.length === 0 ? (
          <div className={styles.noData}>No groups found</div>
        ) : (
          groups.map((group) => (
            <div key={group.sno} className={styles.groupCard}>
              <div className={styles.groupHeader}>
                <div className={styles.groupInfo}>
                  <h3>{group.description}</h3>
                  <div className={styles.groupMeta}>
                    <span className={styles.badge}>{group.itemType}</span>
                    <span className={styles.groupId}>ID: {group.generalGroupId}</span>
                  </div>
                </div>
                
                <div className={styles.actions}>
                  <button
                    className={styles.addButton}
                    onClick={() => handleAddItem(group)}
                  >
                    <span className={styles.buttonIcon}>+</span> Add Item
                  </button>
                  <button
                    className={styles.detailedButton}
                    onClick={() => handleShowDetailed(group)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{marginRight: '6px'}}>
                      <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                    </svg>
                    Show Detailed Items
                  </button>
                  <button
                    className={`${styles.expandButton} ${
                      expandedGroup === group.generalGroupId ? styles.expanded : ''
                    }`}
                    onClick={() => toggleGroup(group)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              {expandedGroup === group.generalGroupId && (
                <div className={styles.groupDetails}>
                  <div className={styles.itemsSection}>
                    <div className={styles.itemsHeader}>
                      <h4>Items in this group ({groupItems[group.generalGroupId]?.length || 0})</h4>
                      <div className={styles.viewToggle}>
                        <button
                          className={`${styles.viewButton} ${viewMode === 'grid' ? styles.viewButtonActive : ''}`}
                          onClick={() => setViewMode('grid')}
                          title="Grid View"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <rect x="1" y="1" width="6" height="6" rx="1"/>
                            <rect x="9" y="1" width="6" height="6" rx="1"/>
                            <rect x="1" y="9" width="6" height="6" rx="1"/>
                            <rect x="9" y="9" width="6" height="6" rx="1"/>
                          </svg>
                        </button>
                        <button
                          className={`${styles.viewButton} ${viewMode === 'table' ? styles.viewButtonActive : ''}`}
                          onClick={() => setViewMode('table')}
                          title="Table View"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <rect x="1" y="2" width="14" height="3" rx="1"/>
                            <rect x="1" y="6.5" width="14" height="3" rx="1"/>
                            <rect x="1" y="11" width="14" height="3" rx="1"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {!groupItems[group.generalGroupId] ? (
                      <div className={styles.loadingItems}>Loading items...</div>
                    ) : groupItems[group.generalGroupId].length === 0 ? (
                      <div className={styles.noItems}>No items found in this group</div>
                    ) : viewMode === 'grid' ? (
                      <div className={styles.itemsGrid}>
                        {groupItems[group.generalGroupId].map((item) => (
                          <div key={item.sno} className={styles.itemCard}>
                            <div className={styles.itemHeader}>
                              <h5>{item.description}</h5>
                              <span className={`${styles.statusBadge} ${styles[item.itemActiveStatus]}`}>
                                {item.itemActiveStatus}
                              </span>
                            </div>
                            <div className={styles.itemDetails}>
                              <div className={styles.itemRow}>
                                <span className={styles.label}>Code:</span>
                                <span>{item.itemCode}</span>
                              </div>
                              <div className={styles.itemRow}>
                                <span className={styles.label}>Type:</span>
                                <span>{item.itemType}</span>
                              </div>
                              {item.itemColor && (
                                <div className={styles.itemRow}>
                                  <span className={styles.label}>Color:</span>
                                  <span>{item.itemColor}</span>
                                </div>
                              )}
                              {item.itemSize && (
                                <div className={styles.itemRow}>
                                  <span className={styles.label}>Size:</span>
                                  <span>{item.itemSize}</span>
                                </div>
                              )}
                              <div className={styles.itemRow}>
                                <span className={styles.label}>Price:</span>
                                <span className={styles.price}>₹{item.price}</span>
                              </div>
                              <div className={styles.itemRow}>
                                <span className={styles.label}>Wholesale:</span>
                                <span>₹{item.wholeSalePrice}</span>
                              </div>
                              <div className={styles.itemRow}>
                                <span className={styles.label}>Quantity:</span>
                                <span className={styles.quantity}>{item.quantity}</span>
                              </div>
                            </div>
                            <button
                              className={styles.ungroupButton}
                              onClick={() => handleUngroup(item)}
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{marginRight: '6px'}}>
                                <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M8 2v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                              Ungroup
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.tableContainer}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Code</th>
                              <th>Type</th>
                              <th>School</th>
                              <th>Color</th>
                              <th>Size</th>
                              <th>Price</th>
                              <th>Wholesale</th>
                              <th>Qty</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupItems[group.generalGroupId].map((item) => (
                              <tr key={item.sno}>
                                <td>{item.description}</td>
                                <td>{item.itemCode}</td>
                                <td>{item.itemType}</td>
                                <td>{item.itemCategory}</td>
                                <td>{item.itemColor || '-'}</td>
                                <td>{item.itemSize || '-'}</td>
                                <td className={styles.price}>₹{item.price}</td>
                                <td>₹{item.wholeSalePrice}</td>
                                <td className={styles.quantity}>{item.quantity}</td>
                                <td>
                                  <span className={`${styles.statusBadge} ${styles[item.itemActiveStatus]}`}>
                                    {item.itemActiveStatus}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className={styles.ungroupButtonSmall}
                                    onClick={() => handleUngroup(item)}
                                  >
                                    Ungroup
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Add Item to Group</h2>
              <button className={styles.closeButton} onClick={closeModal}>×</button>
            </div>
            <div className={styles.modalBody}>
              <p>Group: <strong>{selectedGroup?.description}</strong></p>
              <p>Group ID: <strong>{selectedGroup?.generalGroupId}</strong></p>
              <p className={styles.placeholder}>Item addition form will be implemented here</p>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Group Modal */}
      <DetailedGroupModal
        isOpen={showDetailedModal}
        onClose={closeDetailedModal}
        group={selectedGroup}
        items={selectedGroup ? groupItems[selectedGroup.generalGroupId] : []}
        onUngroup={handleUngroup}
      />
    </div>
  );
};

export default GroupManager;